import { dealerDossiersAnswerReadSchema } from "@hakili/contracts";
import { t } from "@hakili/i18n";

/**
 * `P1-DLW-048` (G-E1): **`readDealerDossiers`, the dealer dossiers READER**, the one place dealer-web reads `GET /api/v1/me/dealership/dossiers` (the dealership's dossier list, `P1-DOS-073`).
 *
 * Lane E owns it; lane B reuses it for the dealer Accueil and the money path, so it stands alone: it takes the request seam (the session's `ApiClient`, or any object with `request`), parses the live answer with the provider's own READ
 * twin (`dealerDossiersAnswerReadSchema`, A48: extra keys tolerated, every required field and type enforced) and never draws from half an answer. A refusal carries the door's own message, `« La connexion a échoué. »`
 * when it said none; an answer the schema refuses is `« Réponse inattendue du serveur. »`.
 *
 * It lives beside the session and not in it, so a screen imports it without importing the session that imports the screen (the boundaries' `no-circular-modules`); `useSession` exposes it as `readDossiers`.
 */
/**
 * What the READ twin parses: the answer with the SERVER's instant `asOf` (P1-DOS-078, OD-17) when the door served it. The strict contract type requires it; the read twin tolerates its absence (an older answer), and a screen then dates nothing.
 */
export type DealerDossiersReadAnswer = ReturnType<typeof dealerDossiersAnswerReadSchema.parse>;

export type DealerDossiersRead =
  | { readonly ok: true; readonly value: DealerDossiersReadAnswer }
  | { readonly ok: false; readonly message: string };

export interface DealerDossiersSeam {
  readonly request: <T>(path: "/api/v1/me/dealership/dossiers", method: "get") => Promise<T>;
}

export async function readDealerDossiers(api: DealerDossiersSeam): Promise<DealerDossiersRead> {
  try {
    const answer = await api.request<unknown>("/api/v1/me/dealership/dossiers", "get");
    const parsed = dealerDossiersAnswerReadSchema.safeParse(answer);
    return parsed.success
      ? { ok: true, value: parsed.data }
      : { ok: false, message: t("common.error.reponseInattendue") };
  } catch (failure) {
    // A `TypeError` is the browser's own English « Failed to fetch »: it falls to the sentence a French screen can say.
    const message = (failure as { message?: unknown } | null)?.message;
    return {
      ok: false,
      message:
        failure instanceof TypeError || typeof message !== "string" || message === ""
          ? t("common.error.connexionEchouee")
          : message,
    };
  }
}
